import {
  Modal,
  ModalFooter,
  ModalBody,
  ModalHeader,
  showToast,
  Banner,
  PasswordInput,
  SelectMenu,
  Button,
  useNexus,
} from '@spotoninc/nexus-react';
import {
  FormControl,
  FormLabel,
  Input,
  Switch,
  VStack,
  HStack,
  Text,
  FormErrorMessage,
  Box,
  Icon,
  IconButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { CloseIcon } from '@spotoninc/nexus-web-assets';
import '../../styles/banner-alignment.css';
import { useFeatureFlagsFromContext } from '../../launchdarkly';

const AddOrEditUserModal = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  roles,
  privileges,
  exclusions,
}) => {
  const [showBanner, setShowBanner] = useState(true);
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
  });
  const [showPasswordErrors, setShowPasswordErrors] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    privilege: '',
    exclusion: '',
    password: '',
    confirmPassword: '',
    changePassword: false,
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    privilege: '',
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const { theme } = useNexus();

  const requirementsRef = useRef(null);

  // Get feature flags
  const flags = useFeatureFlagsFromContext();
  const oktaSsoAuthEnabled = flags?.oktaSsoAuth || false;

  // Check if user is an SSO user (isSpotonUser)
  const isSSOUser = user?.isSpotonUser === true;

  // Computed values for SSO user handling
  const isEmailReadOnly = isSSOUser && oktaSsoAuthEnabled;
  const shouldValidatePassword = !isEmailReadOnly;

  useEffect(() => {
    // Reset validation state when modal opens or closes
    setShowValidation(false);

    if (user) {
      // Find the matching exclusion key
      let exclusionKey = '';
      if (user.exclusions && exclusions && exclusions.length > 0) {
        const match = exclusions.find(
          excl =>
            excl.value === user.exclusions || excl.label === user.exclusions
        );
        if (match) exclusionKey = match.value;
      }
      // Find the matching privilege key using priv_description
      let privilegeKey = '';
      if (user.priv_description && privileges && privileges.length > 0) {
        const match = privileges.find(
          priv => priv.label === user.priv_description
        );
        if (match) privilegeKey = match.value;
      }
      // Find the matching role key using role_description
      let roleKey = '';
      if (user.role_description && roles && roles.length > 0) {
        const match = roles.find(role => role.label === user.role_description);
        if (match) roleKey = match.value;
      }
      setFormData({
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email_id,
        role: roleKey,
        privilege: privilegeKey,
        exclusion: exclusionKey,
        password: '',
        confirmPassword: '',
        changePassword: false,
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        privilege: '',
        exclusion: '',
        password: '',
        confirmPassword: '',
        changePassword: false,
      });
    }
    setErrors({
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      privilege: '',
      password: '',
      confirmPassword: '',
    });
  }, [user, isOpen, exclusions, roles, privileges]);

  // Check form validity whenever form data changes
  useEffect(() => {
    // Update field-level error messages
    updateErrors();

    // Basic validation for required fields
    const passwordValid = shouldValidatePassword
      ? user
        ? !formData.changePassword ||
          (formData.password &&
            formData.confirmPassword &&
            formData.password === formData.confirmPassword &&
            Object.values(passwordRequirements).every(Boolean))
        : formData.password &&
          formData.confirmPassword &&
          formData.password === formData.confirmPassword &&
          Object.values(passwordRequirements).every(Boolean)
      : true; // Password not required for SSO users

    const isValid =
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      String(formData.privilege).trim() !== '' &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
      formData.role !== '' &&
      passwordValid;

    setIsFormValid(isValid);
  }, [formData, passwordRequirements, user, shouldValidatePassword]);

  const checkPasswordRequirements = password => {
    setPasswordRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    });
  };

  const handlePasswordChange = e => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    checkPasswordRequirements(newPassword);
    // setShowPasswordErrors();
  };

  const handlePasswordBlur = () => {
    setShowPasswordRules(false);
    if (formData.password) {
      setShowPasswordErrors(true);
    }
  };

  // Update errors for field-level validation feedback
  const updateErrors = () => {
    // Only show errors if the Save button has been pressed
    if (!showValidation) {
      return;
    }

    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Skip email validation for SSO users when feature flag is enabled
    if (!isEmailReadOnly) {
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    if (shouldValidatePassword && (!user || formData.changePassword)) {
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (!Object.values(passwordRequirements).every(Boolean)) {
        newErrors.password = 'Password does not meet all requirements';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
  };

  const handleSubmit = async () => {
    // Show validation messages when the user tries to submit
    setShowValidation(true);

    // We already validate the form with the disabled button,
    // but let's double-check to be safe
    if (!isFormValid) {
      showToast({
        priority: 'error',
        content: 'Please fill in all required fields',
        autoClose: 5000,
      });
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);

      // Show success message
      showToast({
        priority: 'success',
        content: user ? 'User updated successfully' : 'User added successfully',
        autoClose: 5000,
      });

      // Close the modal after successful save
      // The loading state will remain active until the modal is closed
      onClose();
    } catch (error) {
      console.error('Error saving user:', error);
      showToast({
        priority: 'error',
        content: `Error: ${error.message || 'Unknown error occurred'}`,
        autoClose: 5000,
      });

      // Only reset loading state if there was an error
      setLoading(false);
    }
  };

  return (
    <Modal
      title={user ? 'Edit User' : 'Add New User'}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      className={theme.className}
      data-testid="add-or-edit-user-modal"
    >
      <ModalHeader>
        {showBanner && (
          <Banner
            content={
              <Text>
                {user
                  ? 'Please notify this user about the change to their login credentials.'
                  : 'An invite email will not be sent to new users. Please notify them about their account creation and provide their login credentials.'}
              </Text>
            }
            priority="warning"
            title="User Notification Required"
            onDismiss={() => setShowBanner(false)}
            className="banner-icon-alignment"
          />
        )}
      </ModalHeader>
      <ModalBody>
        <VStack spacing={4}>
          <HStack spacing={4} width="100%">
            <FormControl isRequired isInvalid={!!errors.firstName}>
              <FormLabel>First Name</FormLabel>
              <Input
                value={formData.firstName}
                onChange={e => {
                  setFormData({ ...formData, firstName: e.target.value });
                }}
              />
              <FormErrorMessage>{errors.firstName}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={!!errors.lastName}>
              <FormLabel>Last Name</FormLabel>
              <Input
                value={formData.lastName}
                onChange={e => {
                  setFormData({ ...formData, lastName: e.target.value });
                }}
              />
              <FormErrorMessage>{errors.lastName}</FormErrorMessage>
            </FormControl>
          </HStack>

          <FormControl isRequired={!isEmailReadOnly} isInvalid={!!errors.email}>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={formData.email}
              onChange={e => {
                setFormData({ ...formData, email: e.target.value });
              }}
              readOnly={isEmailReadOnly}
              aria-readonly={isEmailReadOnly}
              opacity={isEmailReadOnly ? 0.6 : 1}
              cursor={isEmailReadOnly ? 'not-allowed' : 'text'}
              _focus={
                isEmailReadOnly
                  ? {
                      borderColor: 'inherit',
                      boxShadow: 'none',
                    }
                  : undefined
              }
            />
            <FormErrorMessage>{errors.email}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.role}>
            <FormLabel>Role</FormLabel>
            <Box overflow="visible">
              <SelectMenu
                initialValue={formData.role}
                value={formData.role}
                placeholder="Select Role"
                options={roles.map(role => ({
                  label: role.label,
                  value: role.value,
                }))}
                onChange={(_, value) => {
                  setFormData({ ...formData, role: value });
                }}
              />
            </Box>
            <FormErrorMessage>{errors.role}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.privilege}>
            <FormLabel>Privilege</FormLabel>
            <Box overflow="visible">
              <SelectMenu
                initialValue={formData.privilege}
                value={formData.privilege}
                placeholder="Select Privilege"
                options={privileges.map(priv => ({
                  label: priv.label,
                  value: priv.value,
                }))}
                onChange={(_, value) => {
                  setFormData({ ...formData, privilege: value });
                }}
              />
            </Box>
          </FormControl>

          <FormControl isInvalid={!!errors.exclusion}>
            <FormLabel>Exclusion List</FormLabel>
            <HStack spacing={2} width="100%">
              <Box flex="1" overflow="visible">
                <SelectMenu
                  initialValue={formData.exclusion}
                  value={formData.exclusion}
                  placeholder="Select Exclusion"
                  options={exclusions.map(excl => ({
                    label: excl.label,
                    value: excl.value,
                  }))}
                  onChange={(_, value) => {
                    setFormData({ ...formData, exclusion: value });
                  }}
                />
              </Box>
              {formData.exclusion && (
                <IconButton
                  size="md"
                  colorScheme="red"
                  variant="solid"
                  aria-label="Remove exclusion"
                  _hover={{ bg: 'red.500' }}
                  icon={
                    <CloseIcon
                      style={{
                        width: '1.4em',
                        height: '1.4em',
                        fontWeight: 'bold',
                      }}
                    />
                  }
                  onClick={() => {
                    setFormData({ ...formData, exclusion: '' });
                  }}
                />
              )}
            </HStack>
          </FormControl>

          {user && !isEmailReadOnly && (
            <FormControl>
              <FormLabel>Change Password</FormLabel>
              <Switch
                checked={formData.changePassword}
                onChange={e => {
                  setFormData({
                    ...formData,
                    changePassword: e.target.checked,
                  });
                }}
              />
            </FormControl>
          )}

          {(user === null || formData.changePassword) && !isEmailReadOnly && (
            <HStack
              spacing={4}
              width="100%"
              align="flex-start"
              flexDirection={['column', 'row']}
            >
              <Box position="relative" width={['100%', '100%', '50%']}>
                <FormControl isRequired isInvalid={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <PasswordInput
                    initialValue={formData.password}
                    onChange={handlePasswordChange}
                    onFocus={() => {
                      setShowPasswordRules(true);
                      setTimeout(() => {
                        if (requirementsRef.current) {
                          requirementsRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                          });
                        }
                      }, 0);
                    }}
                    onBlur={handlePasswordBlur}
                  />
                  {(showPasswordRules ||
                    (showPasswordErrors &&
                      !Object.values(passwordRequirements).every(Boolean))) && (
                    <Box
                      ref={requirementsRef}
                      mt={2}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      position={['static', 'static', 'absolute']}
                      left={0}
                      right={0}
                      top={['unset', 'unset', '100%']}
                      zIndex={1}
                      width="100%"
                    >
                      <Wrap spacing={4} width="100%">
                        {(!passwordRequirements.length ||
                          showPasswordRules) && (
                          <WrapItem>
                            <HStack>
                              <Icon
                                as={
                                  passwordRequirements.length
                                    ? CheckCircleIcon
                                    : WarningIcon
                                }
                                color={
                                  passwordRequirements.length
                                    ? 'green.500'
                                    : showPasswordErrors
                                      ? 'red.500'
                                      : 'gray.500'
                                }
                              />
                              <Text>8 characters</Text>
                            </HStack>
                          </WrapItem>
                        )}
                        {(!passwordRequirements.uppercase ||
                          showPasswordRules) && (
                          <WrapItem>
                            <HStack>
                              <Icon
                                as={
                                  passwordRequirements.uppercase
                                    ? CheckCircleIcon
                                    : WarningIcon
                                }
                                color={
                                  passwordRequirements.uppercase
                                    ? 'green.500'
                                    : showPasswordErrors
                                      ? 'red.500'
                                      : 'gray.500'
                                }
                              />
                              <Text>Uppercase</Text>
                            </HStack>
                          </WrapItem>
                        )}
                        {(!passwordRequirements.lowercase ||
                          showPasswordRules) && (
                          <WrapItem>
                            <HStack>
                              <Icon
                                as={
                                  passwordRequirements.lowercase
                                    ? CheckCircleIcon
                                    : WarningIcon
                                }
                                color={
                                  passwordRequirements.lowercase
                                    ? 'green.500'
                                    : showPasswordErrors
                                      ? 'red.500'
                                      : 'gray.500'
                                }
                              />
                              <Text>Lowercase</Text>
                            </HStack>
                          </WrapItem>
                        )}
                        {(!passwordRequirements.number ||
                          showPasswordRules) && (
                          <WrapItem>
                            <HStack>
                              <Icon
                                as={
                                  passwordRequirements.number
                                    ? CheckCircleIcon
                                    : WarningIcon
                                }
                                color={
                                  passwordRequirements.number
                                    ? 'green.500'
                                    : showPasswordErrors
                                      ? 'red.500'
                                      : 'gray.500'
                                }
                              />
                              <Text>Number</Text>
                            </HStack>
                          </WrapItem>
                        )}
                      </Wrap>
                    </Box>
                  )}
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>
              </Box>
              <FormControl
                isRequired
                isInvalid={!!errors.confirmPassword}
                width={['100%', '100%', '50%']}
              >
                <FormLabel>Confirm Password</FormLabel>
                <PasswordInput
                  initialValue={formData.confirmPassword}
                  onChange={e => {
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    });
                  }}
                />
                <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
              </FormControl>
            </HStack>
          )}
        </VStack>
      </ModalBody>

      <ModalFooter>
        <Button variant="tonal" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          disabled={!isFormValid}
        >
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddOrEditUserModal;
